import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Wallet, Plus, Edit2, Trash2, Save, RefreshCw, Calendar,
  Building2, DollarSign, TrendingUp, TrendingDown, AlertCircle,
  ChevronRight, Check, X, Loader2, ArrowRightLeft, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Broker Management Dialog
const BrokerManagementDialog = ({ brokers, onBrokersChange }) => {
  const [open, setOpen] = useState(false);
  const [newBrokerName, setNewBrokerName] = useState('');
  const [newBrokerDesc, setNewBrokerDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const handleAddBroker = async () => {
    if (!newBrokerName.trim()) return;
    
    setLoading(true);
    try {
      await api.post('/api/wealth/brokers', {
        name: newBrokerName.trim(),
        description: newBrokerDesc.trim(),
        display_order: brokers.length + 1
      });
      toast.success('Broker added successfully');
      setNewBrokerName('');
      setNewBrokerDesc('');
      onBrokersChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add broker');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBroker = async (brokerId) => {
    if (!editName.trim()) return;
    
    setLoading(true);
    try {
      await api.put(`/api/wealth/brokers/${brokerId}`, { name: editName.trim() });
      toast.success('Broker updated');
      setEditingId(null);
      onBrokersChange();
    } catch (error) {
      toast.error('Failed to update broker');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBroker = async (brokerId) => {
    if (!window.confirm('Are you sure you want to deactivate this broker?')) return;
    
    setLoading(true);
    try {
      await api.delete(`/api/wealth/brokers/${brokerId}`);
      toast.success('Broker deactivated');
      onBrokersChange();
    } catch (error) {
      toast.error('Failed to deactivate broker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="manage-brokers-btn">
          <Building2 className="w-4 h-4" />
          Manage Brokers
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cyan-400" />
            Manage External Brokers
          </DialogTitle>
          <DialogDescription>
            Add, edit, or remove brokers from the master list
          </DialogDescription>
        </DialogHeader>
        
        {/* Add New Broker */}
        <div className="space-y-3 p-4 bg-white/5 rounded-lg border border-white/10">
          <p className="text-sm font-medium">Add New Broker</p>
          <div className="flex gap-2">
            <Input
              placeholder="Broker name"
              value={newBrokerName}
              onChange={(e) => setNewBrokerName(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleAddBroker} disabled={loading || !newBrokerName.trim()}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Input
            placeholder="Description (optional)"
            value={newBrokerDesc}
            onChange={(e) => setNewBrokerDesc(e.target.value)}
          />
        </div>

        {/* Broker List */}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {brokers.map((broker) => (
            <div 
              key={broker.id} 
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
            >
              {editingId === broker.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1"
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={() => handleUpdateBroker(broker.id)}>
                    <Check className="w-4 h-4 text-emerald-400" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    <X className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{broker.name}</p>
                    {broker.description && (
                      <p className="text-xs text-muted-foreground">{broker.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setEditingId(broker.id); setEditName(broker.name); }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteBroker(broker.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
          {brokers.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No brokers added yet</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Compare Dates Dialog
const CompareDatesDialog = ({ snapshots }) => {
  const [open, setOpen] = useState(false);
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  // Set default dates when dialog opens
  useEffect(() => {
    if (open && snapshots.length >= 2) {
      setDate1(snapshots[1]?.snapshot_date || '');
      setDate2(snapshots[0]?.snapshot_date || '');
    } else if (open && snapshots.length === 1) {
      setDate2(snapshots[0]?.snapshot_date || '');
    }
  }, [open, snapshots]);

  const handleCompare = async () => {
    if (!date1 || !date2) {
      toast.error('Please select both dates');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/api/wealth/compare?date1=${date1}&date2=${date2}`);
      setComparison(response.data);
    } catch (error) {
      toast.error('Failed to compare snapshots');
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <ArrowUp className="w-4 h-4 text-emerald-400" />;
    if (change < 0) return <ArrowDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-emerald-400';
    if (change < 0) return 'text-red-400';
    return 'text-muted-foreground';
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="compare-dates-btn">
          <ArrowRightLeft className="w-4 h-4" />
          Compare Dates
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-cyan-400" />
            Compare Parked Funds Between Dates
          </DialogTitle>
          <DialogDescription>
            Track fund movements and changes between two snapshot dates
          </DialogDescription>
        </DialogHeader>
        
        {/* Date Selection */}
        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex-1">
            <label className="text-sm text-muted-foreground mb-1 block">From Date</label>
            <input
              type="date"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <ArrowRightLeft className="w-6 h-6 text-muted-foreground mt-5" />
          <div className="flex-1">
            <label className="text-sm text-muted-foreground mb-1 block">To Date</label>
            <input
              type="date"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          <Button onClick={handleCompare} disabled={loading || !date1 || !date2} className="mt-5">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}
          </Button>
        </div>

        {/* Comparison Results */}
        {comparison && (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="glass border-white/10">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">{formatDisplayDate(comparison.date1)}</p>
                  <p className="text-xl font-bold">{formatCurrency(comparison.date1_total)}</p>
                </CardContent>
              </Card>
              <Card className="glass border-white/10">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">{formatDisplayDate(comparison.date2)}</p>
                  <p className="text-xl font-bold">{formatCurrency(comparison.date2_total)}</p>
                </CardContent>
              </Card>
              <Card className={`glass border-white/10 ${comparison.total_change >= 0 ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`}>
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Net Change</p>
                  <p className={`text-xl font-bold ${getChangeColor(comparison.total_change)}`}>
                    {comparison.total_change >= 0 ? '+' : ''}{formatCurrency(comparison.total_change)}
                  </p>
                  <p className={`text-xs ${getChangeColor(comparison.total_change)}`}>
                    {comparison.total_change_pct >= 0 ? '+' : ''}{comparison.total_change_pct.toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card className="glass border-white/10">
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Summary</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-emerald-400">↑{comparison.summary.increases}</span>
                    <span className="text-red-400">↓{comparison.summary.decreases}</span>
                    <span className="text-muted-foreground">={comparison.summary.unchanged}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Broker Comparison Table */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium">Broker</th>
                    <th className="text-right py-3 px-4 font-medium">{formatDisplayDate(comparison.date1)}</th>
                    <th className="text-right py-3 px-4 font-medium">{formatDisplayDate(comparison.date2)}</th>
                    <th className="text-right py-3 px-4 font-medium">Change</th>
                    <th className="text-right py-3 px-4 font-medium">%</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.brokers.map((broker, idx) => (
                    <tr key={broker.broker_id} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="py-3 px-4 font-medium">{broker.broker_name}</td>
                      <td className="text-right py-3 px-4 text-muted-foreground">
                        {formatCurrency(broker.date1_amount)}
                      </td>
                      <td className="text-right py-3 px-4">
                        {formatCurrency(broker.date2_amount)}
                      </td>
                      <td className={`text-right py-3 px-4 font-medium ${getChangeColor(broker.change)}`}>
                        <div className="flex items-center justify-end gap-1">
                          {getChangeIcon(broker.change)}
                          {broker.change >= 0 ? '+' : ''}{formatCurrency(broker.change)}
                        </div>
                      </td>
                      <td className={`text-right py-3 px-4 ${getChangeColor(broker.change)}`}>
                        {broker.change_pct >= 0 ? '+' : ''}{broker.change_pct.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-white/5 font-semibold">
                  <tr className="border-t-2 border-white/20">
                    <td className="py-3 px-4">TOTAL</td>
                    <td className="text-right py-3 px-4">{formatCurrency(comparison.date1_total)}</td>
                    <td className="text-right py-3 px-4">{formatCurrency(comparison.date2_total)}</td>
                    <td className={`text-right py-3 px-4 ${getChangeColor(comparison.total_change)}`}>
                      <div className="flex items-center justify-end gap-1">
                        {getChangeIcon(comparison.total_change)}
                        {comparison.total_change >= 0 ? '+' : ''}{formatCurrency(comparison.total_change)}
                      </div>
                    </td>
                    <td className={`text-right py-3 px-4 ${getChangeColor(comparison.total_change)}`}>
                      {comparison.total_change_pct >= 0 ? '+' : ''}{comparison.total_change_pct.toFixed(1)}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Add/Minus Comparison */}
            {(comparison.date1_add_minus !== 0 || comparison.date2_add_minus !== 0) && (
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-sm">
                  <span className="text-muted-foreground">Add/Minus adjustments: </span>
                  <span>{formatDisplayDate(comparison.date1)}: {formatCurrency(comparison.date1_add_minus)}</span>
                  <span className="mx-2">→</span>
                  <span>{formatDisplayDate(comparison.date2)}: {formatCurrency(comparison.date2_add_minus)}</span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!comparison && !loading && (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground">
            <div className="text-center">
              <ArrowRightLeft className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Select two dates and click Compare to see fund movements</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// Main Wealth Management Page
const WealthManagement = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [brokers, setBrokers] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedAmounts, setEditedAmounts] = useState({});
  const [addMinus, setAddMinus] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [recentSnapshots, setRecentSnapshots] = useState([]);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Fetch recent snapshots for comparison dialog
  const fetchRecentSnapshots = useCallback(async () => {
    try {
      const response = await api.get('/api/wealth/snapshots?limit=30');
      setRecentSnapshots(response.data);
    } catch (error) {
      console.error('Failed to fetch snapshots:', error);
    }
  }, []);

  // Fetch brokers
  const fetchBrokers = useCallback(async () => {
    try {
      const response = await api.get('/api/wealth/brokers');
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
      // If 403, brokers may not be seeded yet
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      }
    }
  }, []);

  // Fetch snapshot for selected date
  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/wealth/snapshots/${selectedDate}`);
      setSnapshot(response.data);
      
      // Initialize edited amounts from snapshot
      const amounts = {};
      (response.data.entries || []).forEach(entry => {
        amounts[entry.broker_id] = entry.amount;
      });
      setEditedAmounts(amounts);
      setAddMinus(response.data.add_minus || 0);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch snapshot:', error);
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Seed initial data
  const seedData = async () => {
    try {
      await api.post('/api/wealth/seed-data');
      toast.success('Initial data seeded successfully');
      await fetchBrokers();
      await fetchSnapshot();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  // Save snapshot
  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const entries = brokers.map(broker => ({
        broker_id: broker.id,
        amount: editedAmounts[broker.id] || 0
      }));

      await api.post('/api/wealth/snapshots', {
        snapshot_date: selectedDate,
        entries,
        add_minus: addMinus,
        notes: ''
      });

      toast.success('Snapshot saved successfully');
      setHasChanges(false);
      await fetchSnapshot();
    } catch (error) {
      toast.error('Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  };

  // Handle amount change
  const handleAmountChange = (brokerId, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedAmounts(prev => ({ ...prev, [brokerId]: numValue }));
    setHasChanges(true);
  };

  // Calculate totals
  const calculateCapital = () => {
    return Object.values(editedAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
  };

  const calculateTotal = () => {
    return calculateCapital() + addMinus;
  };

  // Load data on mount
  useEffect(() => {
    if (isAdmin) {
      fetchBrokers();
      fetchRecentSnapshots();
    }
  }, [isAdmin, fetchBrokers, fetchRecentSnapshots]);

  useEffect(() => {
    if (isAdmin && brokers.length > 0) {
      fetchSnapshot();
    }
  }, [isAdmin, selectedDate, brokers.length, fetchSnapshot]);

  // Format date for display (DD/MM/YYYY)
  const formatDisplayDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="glass border-white/10 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Super Admin access required for Wealth Management</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wealth-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-7 h-7 text-cyan-400" />
            Wealth Management
          </h1>
          <p className="text-muted-foreground">Parked Funds Snapshot - Track fund allocations across external brokers</p>
        </div>
        <div className="flex items-center gap-2">
          <CompareDatesDialog snapshots={recentSnapshots} />
          <BrokerManagementDialog brokers={brokers} onBrokersChange={fetchBrokers} />
          {brokers.length === 0 && (
            <Button onClick={seedData} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Seed Initial Data
            </Button>
          )}
          <Button 
            onClick={saveSnapshot} 
            disabled={!hasChanges || saving}
            className="gap-2"
            data-testid="save-snapshot-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Snapshot
          </Button>
        </div>
      </div>

      {/* Date Selector & Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Date Selector Card */}
        <Card className="glass border-white/10 border-t-4 border-t-amber-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-amber-400" />
              <span className="text-sm font-medium">Snapshot Date</span>
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-lg font-bold text-amber-400"
              data-testid="snapshot-date-input"
            />
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {formatDisplayDate(selectedDate)}
            </p>
          </CardContent>
        </Card>

        {/* Capital Card */}
        <Card className="glass border-white/10 border-t-4 border-t-emerald-400">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Capital</p>
            <p className="text-3xl font-bold text-emerald-400" data-testid="capital-total">
              {formatCurrency(calculateCapital())}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Sum of all parked funds</p>
          </CardContent>
        </Card>

        {/* Add/Minus Card */}
        <Card className="glass border-white/10 border-t-4 border-t-cyan-400">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Add/Minus</p>
            <Input
              type="number"
              value={addMinus}
              onChange={(e) => { setAddMinus(parseFloat(e.target.value) || 0); setHasChanges(true); }}
              className="text-2xl font-bold h-10 bg-cyan-500/10 border-cyan-500/30"
              data-testid="add-minus-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Adjustments for the day</p>
          </CardContent>
        </Card>

        {/* Total Card */}
        <Card className="glass border-white/10 border-t-4 border-t-purple-400">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Total</p>
            <p className="text-3xl font-bold text-purple-400" data-testid="grand-total">
              {formatCurrency(calculateTotal())}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Capital + Add/Minus</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Snapshot Table */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-cyan-400" />
            Parked Funds by Broker
            <Badge variant="outline" className="ml-2">{formatDisplayDate(selectedDate)}</Badge>
          </CardTitle>
          <CardDescription>
            Click on any amount to edit. Changes are saved when you click &quot;Save Snapshot&quot;.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              <span className="ml-2">Loading snapshot...</span>
            </div>
          ) : brokers.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No brokers configured yet</p>
              <Button onClick={seedData} className="gap-2">
                <Plus className="w-4 h-4" />
                Seed Initial Data from Excel
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b border-white/10">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Broker Name</div>
                <div className="col-span-4 text-right">Parked Amount</div>
                <div className="col-span-2 text-right">% of Total</div>
              </div>

              {/* Broker Rows */}
              {brokers.map((broker, index) => {
                const amount = editedAmounts[broker.id] || 0;
                const capital = calculateCapital();
                const percentage = capital > 0 ? ((amount / capital) * 100).toFixed(1) : '0.0';
                
                return (
                  <div 
                    key={broker.id}
                    className={`grid grid-cols-12 gap-4 px-4 py-3 rounded-lg border transition-colors ${
                      amount > 0 
                        ? 'bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                    data-testid={`broker-row-${broker.id}`}
                  >
                    <div className="col-span-1 flex items-center text-muted-foreground">
                      {index + 1}
                    </div>
                    <div className="col-span-5 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{broker.name}</span>
                    </div>
                    <div className="col-span-4 flex items-center justify-end">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          value={amount}
                          onChange={(e) => handleAmountChange(broker.id, e.target.value)}
                          className={`w-40 text-right pr-3 pl-7 font-mono ${
                            amount > 0 ? 'text-emerald-400 border-emerald-500/30' : ''
                          }`}
                          data-testid={`amount-input-${broker.id}`}
                        />
                      </div>
                    </div>
                    <div className="col-span-2 flex items-center justify-end">
                      <Badge 
                        variant="outline" 
                        className={amount > 0 ? 'text-emerald-400 border-emerald-500/30' : ''}
                      >
                        {percentage}%
                      </Badge>
                    </div>
                  </div>
                );
              })}

              {/* Total Row */}
              <div className="grid grid-cols-12 gap-4 px-4 py-4 mt-4 rounded-lg bg-purple-500/10 border-2 border-purple-500/30">
                <div className="col-span-1"></div>
                <div className="col-span-5 font-bold text-purple-400 flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  TOTAL
                </div>
                <div className="col-span-4 text-right font-bold text-2xl text-purple-400">
                  {formatCurrency(calculateTotal())}
                </div>
                <div className="col-span-2 text-right">
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                    100%
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Snapshots */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-400" />
            Recent Snapshots
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RecentSnapshots 
            selectedDate={selectedDate} 
            onSelectDate={setSelectedDate} 
          />
        </CardContent>
      </Card>

      {/* Status indicator for unsaved changes */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-amber-500/90 text-black px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">You have unsaved changes</span>
          <Button size="sm" variant="secondary" onClick={saveSnapshot} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Now'}
          </Button>
        </div>
      )}
    </div>
  );
};


// Recent Snapshots Component
const RecentSnapshots = ({ selectedDate, onSelectDate }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const response = await api.get('/api/wealth/snapshots?limit=10');
        setSnapshots(response.data);
      } catch (error) {
        console.error('Failed to fetch snapshots:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSnapshots();
  }, [selectedDate]);

  if (loading) {
    return <div className="text-center py-4"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  if (snapshots.length === 0) {
    return <p className="text-center text-muted-foreground py-4">No snapshots saved yet</p>;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {snapshots.map((s) => {
        const isSelected = s.snapshot_date === selectedDate;
        const [year, month, day] = s.snapshot_date.split('-');
        
        return (
          <button
            key={s.id}
            onClick={() => onSelectDate(s.snapshot_date)}
            className={`p-3 rounded-lg border text-left transition-all ${
              isSelected 
                ? 'bg-cyan-500/20 border-cyan-500/50' 
                : 'bg-white/5 border-white/10 hover:bg-white/10'
            }`}
          >
            <p className={`text-sm font-medium ${isSelected ? 'text-cyan-400' : ''}`}>
              {day}/{month}/{year}
            </p>
            <p className={`text-lg font-bold ${isSelected ? 'text-cyan-400' : 'text-emerald-400'}`}>
              {formatCurrency(s.total)}
            </p>
            <p className="text-xs text-muted-foreground">{s.broker_count} brokers</p>
          </button>
        );
      })}
    </div>
  );
};

export default WealthManagement;
